import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../utils/api";
import Loader from "../../../ui/Loader";
import Modal from "../../../ui/Modal";
import Table from "../../../ui/Table";
import {
  swalError,
  swalSuccess,
  swalConfirm,
} from "../../../../utils/swalAlert";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const DEFAULT_GRADE_MAPPING = [
  { minPercentage: 90, maxPercentage: 100, grade: "A+", remark: "Outstanding" },
  { minPercentage: 80, maxPercentage: 89, grade: "A", remark: "Excellent" },
  { minPercentage: 70, maxPercentage: 79, grade: "B+", remark: "Very Good" },
  { minPercentage: 60, maxPercentage: 69, grade: "B", remark: "Good" },
  { minPercentage: 50, maxPercentage: 59, grade: "C", remark: "Satisfactory" },
  { minPercentage: 40, maxPercentage: 49, grade: "D", remark: "Needs Improvement" },
  { minPercentage: 0, maxPercentage: 39, grade: "F", remark: "Unsatisfactory" },
];

const ManageGradeConfig = () => {
  const { user } = useContext(AuthContext);
  const [gradeMapping, setGradeMapping] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form states
  const initialFormState = { minPercentage: "", maxPercentage: "", grade: "", remark: "" };
  const [formData, setFormData] = useState(initialFormState);
  const [editIndex, setEditIndex] = useState(null);

  useEffect(() => {
    const fetchInstitute = async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch institute data");
        const data = await response.json();
        const mapping = data.gradeLetterMapping || [];
        setGradeMapping(mapping);
        setError(null);
      } catch (err) {
        setError(err.message);
        swalError("Failed to load grade configuration.");
      } finally {
        setLoading(false);
      }
    };
    fetchInstitute();
  }, [user.instituteId]);

  const updateGradeMappingOnServer = async (updatedMapping, successMsg) => {
    setIsUpdating(true);
    try {
      const form = new FormData();
      form.append("gradeLetterMapping", JSON.stringify(updatedMapping));
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/update/${user.instituteId}`,
        {
          method: "PUT",
          body: form,
        }
      );
      if (!response) return;
      if (!response.ok) {
        throw new Error("Failed to update grade mapping");
      }
      setGradeMapping(updatedMapping);
      swalSuccess(successMsg);
      setIsEditModalOpen(false);
      setIsModalOpen(false);
      setFormData(initialFormState);
    } catch (err) {
      swalError("Failed to update grade mapping.");
    } finally {
      setIsUpdating(false);
      setIsCreating(false);
      setIsDeleting(false);
    }
  };

  const validateForm = (data) => {
    if (data.minPercentage === "" || data.maxPercentage === "") {
      swalError("Please provide both minimum and maximum percentages.");
      return false;
    }
    if (!data.grade.trim() || !data.remark.trim()) {
      swalError("Please provide both grade letter and remark.");
      return false;
    }
    const min = Number(data.minPercentage);
    const max = Number(data.maxPercentage);
    if (min > max) {
      swalError("Minimum percentage cannot be greater than maximum percentage.");
      return false;
    }
    if (min < 0 || min > 100 || max < 0 || max > 100) {
      swalError("Percentages must be between 0 and 100.");
      return false;
    }

    // Check for overlap, excluding the current edit index
    const otherGrades = gradeMapping.filter((_, i) => i !== editIndex);
    for (const g of otherGrades) {
      const gMin = Number(g.minPercentage);
      const gMax = Number(g.maxPercentage);
      if (Math.max(min, gMin) <= Math.min(max, gMax)) {
        swalError(`Range ${min}-${max}% overlaps with existing grade "${g.grade}" (${gMin}-${gMax}%).`);
        return false;
      }
    }

    return true;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validateForm(formData)) return;
    setIsCreating(true);
    const updatedMapping = [...gradeMapping, {
      minPercentage: Number(formData.minPercentage),
      maxPercentage: Number(formData.maxPercentage),
      grade: formData.grade.trim(),
      remark: formData.remark.trim()
    }];
    await updateGradeMappingOnServer(updatedMapping, "Grade range created successfully.");
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!validateForm(formData)) return;
    setIsUpdating(true);
    const updatedMapping = [...gradeMapping];
    updatedMapping[editIndex] = {
      minPercentage: Number(formData.minPercentage),
      maxPercentage: Number(formData.maxPercentage),
      grade: formData.grade.trim(),
      remark: formData.remark.trim()
    };
    await updateGradeMappingOnServer(updatedMapping, "Grade range updated successfully.");
  };

  const handleDelete = async (index) => {
    const result = await swalConfirm(
      "Are you sure?",
      "You won't be able to revert this!",
      "Yes, delete it!",
      "Cancel"
    );
    if (result.isConfirmed) {
      setIsDeleting(true);
      const updatedMapping = gradeMapping.filter((_, i) => i !== index);
      await updateGradeMappingOnServer(updatedMapping, "Grade range deleted successfully.");
    }
  };

  const loadDefaults = async () => {
    const result = await swalConfirm(
      "Load Default Grades?",
      "This will replace your current grade configuration with the standard defaults.",
      "Yes, load defaults",
      "Cancel"
    );
    if (result.isConfirmed) {
      await updateGradeMappingOnServer(DEFAULT_GRADE_MAPPING, "Default grades loaded successfully.");
    }
  };

  const columns = [
    { key: "range", label: "Percentage Range", sortable: false },
    { key: "grade", label: "Grade", sortable: false },
    { key: "remark", label: "Remark", sortable: false },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  const sortedMapping = [...gradeMapping].sort((a, b) => Number(b.minPercentage) - Number(a.minPercentage));

  const tableData = sortedMapping.map((lvl) => {
    // Find original index for editing/deleting
    const originalIndex = gradeMapping.findIndex(
      g => g.minPercentage === lvl.minPercentage && g.grade === lvl.grade
    );
    
    return {
      range: `${lvl.minPercentage}% – ${lvl.maxPercentage}%`,
      grade: <span style={{ fontWeight: 800, color: "#f09a21" }}>{lvl.grade}</span>,
      remark: lvl.remark,
      edit: (
        <button
          className="edit-btn"
          onClick={() => {
            setEditIndex(originalIndex);
            setFormData({
              minPercentage: lvl.minPercentage,
              maxPercentage: lvl.maxPercentage,
              grade: lvl.grade,
              remark: lvl.remark
            });
            setIsEditModalOpen(true);
          }}
          disabled={isDeleting || isUpdating || isCreating}
        >
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
      delete: (
        <button
          className="delete-btn"
          onClick={() => handleDelete(originalIndex)}
          disabled={isDeleting || isUpdating || isCreating}
        >
          <i className="fa-solid fa-trash-can"></i>
        </button>
      ),
    };
  });

  return (
    <>
      <div className="manage-container">
        <div className="header">
          <button 
            className="create-btn" 
            style={{ background: '#fff', color: '#f09a21', border: '1px solid #f09a21' }} 
            onClick={loadDefaults}
            disabled={isCreating || isUpdating}
          >
            Load Defaults
          </button>
          <button className="create-btn" onClick={() => {
            setFormData(initialFormState);
            setIsModalOpen(true);
          }}>
            Add Grade Range +
          </button>
        </div>
      </div>

      <div className="manage-container">
        {loading && <Loader fontSize="2rem" />}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && (
          <Table data={tableData} columns={columns} caption="Grade Configuration" />
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => !isCreating && setIsModalOpen(false)}>
        <h2>Add Grade Range</h2>
        <form onSubmit={handleCreate} className="department-form">
          <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label><i className="fa-solid fa-asterisk requiredIcon"></i> Min %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.minPercentage}
                placeholder="e.g. 80"
                onChange={(e) => setFormData({...formData, minPercentage: e.target.value})}
                required
                disabled={isCreating}
              />
            </div>
            <div>
              <label><i className="fa-solid fa-asterisk requiredIcon"></i> Max %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.maxPercentage}
                placeholder="e.g. 89"
                onChange={(e) => setFormData({...formData, maxPercentage: e.target.value})}
                required
                disabled={isCreating}
              />
            </div>
          </div>
          <div className="form-group">
            <label><i className="fa-solid fa-asterisk requiredIcon"></i> Grade Letter</label>
            <input
              type="text"
              value={formData.grade}
              placeholder="e.g. A"
              onChange={(e) => setFormData({...formData, grade: e.target.value})}
              required
              disabled={isCreating}
              maxLength={5}
            />
          </div>
          <div className="form-group">
            <label><i className="fa-solid fa-asterisk requiredIcon"></i> Remark</label>
            <input
              type="text"
              value={formData.remark}
              placeholder="e.g. Excellent"
              onChange={(e) => setFormData({...formData, remark: e.target.value})}
              required
              disabled={isCreating}
            />
          </div>
          {isCreating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Create Grade Range
            </button>
          )}
        </form>
      </Modal>

      <Modal isOpen={isEditModalOpen} onClose={() => !isUpdating && setIsEditModalOpen(false)}>
        <h2>Edit Grade Range</h2>
        <form onSubmit={handleEdit} className="department-form">
          <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label><i className="fa-solid fa-asterisk requiredIcon"></i> Min %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.minPercentage}
                onChange={(e) => setFormData({...formData, minPercentage: e.target.value})}
                required
                disabled={isUpdating}
              />
            </div>
            <div>
              <label><i className="fa-solid fa-asterisk requiredIcon"></i> Max %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.maxPercentage}
                onChange={(e) => setFormData({...formData, maxPercentage: e.target.value})}
                required
                disabled={isUpdating}
              />
            </div>
          </div>
          <div className="form-group">
            <label><i className="fa-solid fa-asterisk requiredIcon"></i> Grade Letter</label>
            <input
              type="text"
              value={formData.grade}
              onChange={(e) => setFormData({...formData, grade: e.target.value})}
              required
              disabled={isUpdating}
              maxLength={5}
            />
          </div>
          <div className="form-group">
            <label><i className="fa-solid fa-asterisk requiredIcon"></i> Remark</label>
            <input
              type="text"
              value={formData.remark}
              onChange={(e) => setFormData({...formData, remark: e.target.value})}
              required
              disabled={isUpdating}
            />
          </div>
          {isUpdating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Update Grade Range
            </button>
          )}
        </form>
      </Modal>
    </>
  );
};

export default ManageGradeConfig;
